/**
 * Shared UI primitives. OWNER: W0 — treat as stable API.
 *
 * Deliberately small and unopinionated: the ribbon, panels and tabs are all
 * built from these, so every workstream renders with the same metrics and
 * palette. Add a new primitive here rather than restyling one locally.
 */

import * as React from "react";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------- button ---

type ButtonVariant = "default" | "primary" | "ghost" | "outline" | "danger" | "success";
type ButtonSize = "sm" | "md" | "lg";

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  default:
    "bg-ads-surface border border-ads-border-strong text-ads-text hover:bg-ads-hover active:bg-ads-active",
  primary: "bg-ads-accent text-white hover:bg-ads-accent-hover active:bg-ads-accent-hover border border-ads-accent",
  ghost: "bg-transparent text-ads-text hover:bg-ads-hover active:bg-ads-active",
  outline: "bg-transparent border border-ads-border-strong text-ads-text hover:bg-ads-hover",
  danger: "bg-red-600 text-white hover:bg-red-700 border border-red-600",
  success: "bg-green-600 text-white hover:bg-green-700 border border-green-600",
};

const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: "h-7 px-2 text-xs gap-1",
  md: "h-8 px-3 text-sm gap-1.5",
  lg: "h-9 px-4 text-sm gap-2",
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "default", size = "md", ...props }, ref) => (
    <button
      ref={ref}
      className={cn(
        "inline-flex items-center justify-center rounded font-medium whitespace-nowrap transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ads-accent focus-visible:ring-offset-1",
        "disabled:pointer-events-none disabled:opacity-40",
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        className,
      )}
      {...props}
    />
  ),
);
Button.displayName = "Button";

// ------------------------------------------------------------------ card ---

export function Card({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("rounded-md border border-ads-border bg-ads-surface shadow-sm", className)}
      {...props}
    />
  );
}

export function CardHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("flex items-center justify-between border-b border-ads-border px-4 py-2.5", className)}
      {...props}
    />
  );
}

export function CardTitle({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return <h3 className={cn("text-sm font-semibold text-ads-text", className)} {...props} />;
}

export function CardBody({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("px-4 py-3", className)} {...props} />;
}

// ----------------------------------------------------------------- badge ---

type BadgeTone = "neutral" | "blue" | "green" | "amber" | "red";

const BADGE_TONES: Record<BadgeTone, string> = {
  neutral: "bg-ads-hover text-ads-text border-ads-border",
  blue: "bg-ads-accent-soft text-ads-accent border-ads-accent-border",
  green: "bg-green-50 text-green-700 border-green-200",
  amber: "bg-amber-50 text-amber-800 border-amber-200",
  red: "bg-red-50 text-red-700 border-red-200",
};

export function Badge({
  tone = "neutral",
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement> & { tone?: BadgeTone }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded border px-1.5 py-0.5 text-[11px] font-medium leading-none",
        BADGE_TONES[tone],
        className,
      )}
      {...props}
    />
  );
}

/** Small coloured dot used across the status bar and status cards. */
export function StatusDot({ tone = "neutral", className }: { tone?: BadgeTone; className?: string }) {
  const colors: Record<BadgeTone, string> = {
    neutral: "bg-neutral-400",
    blue: "bg-ads-accent",
    green: "bg-green-500",
    amber: "bg-amber-500",
    red: "bg-red-500",
  };
  return <span className={cn("inline-block h-2 w-2 shrink-0 rounded-full", colors[tone], className)} />;
}

// ----------------------------------------------------------------- input ---

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...props }, ref) => (
    <input
      ref={ref}
      className={cn(
        "h-8 w-full rounded border border-ads-border-strong bg-ads-surface px-2 text-sm text-ads-text",
        "placeholder:text-ads-faint focus:border-ads-accent focus:outline-none focus:ring-1 focus:ring-ads-accent",
        "disabled:bg-ads-bg disabled:text-ads-faint",
        className,
      )}
      {...props}
    />
  ),
);
Input.displayName = "Input";

export const Select = React.forwardRef<
  HTMLSelectElement,
  React.SelectHTMLAttributes<HTMLSelectElement>
>(({ className, ...props }, ref) => (
  <select
    ref={ref}
    className={cn(
      "h-8 w-full rounded border border-ads-border-strong bg-ads-surface px-2 text-sm text-ads-text",
      "focus:border-ads-accent focus:outline-none focus:ring-1 focus:ring-ads-accent",
      "disabled:bg-ads-bg disabled:text-ads-faint",
      className,
    )}
    {...props}
  />
));
Select.displayName = "Select";

export function Checkbox({ className, ...props }: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      type="checkbox"
      className={cn("h-3.5 w-3.5 rounded border-ads-border-strong text-ads-accent focus:ring-ads-accent", className)}
      {...props}
    />
  );
}

export function Field({
  label,
  hint,
  className,
  children,
}: {
  label: string;
  hint?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={cn("flex flex-col gap-1", className)}>
      <span className="text-xs font-medium text-ads-muted">{label}</span>
      {children}
      {hint ? <span className="text-[11px] text-ads-faint">{hint}</span> : null}
    </label>
  );
}

// ----------------------------------------------------------------- table ---

export function Table({ className, ...props }: React.TableHTMLAttributes<HTMLTableElement>) {
  return (
    <div className="w-full overflow-x-auto">
      <table className={cn("w-full border-collapse text-sm", className)} {...props} />
    </div>
  );
}

export function Th({ className, ...props }: React.ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      className={cn(
        "border-b border-ads-border px-3 py-2 text-left text-xs font-semibold text-ads-muted",
        className,
      )}
      {...props}
    />
  );
}

export function Td({ className, ...props }: React.TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <td className={cn("border-b border-ads-border px-3 py-1.5 text-ads-text", className)} {...props} />
  );
}

// -------------------------------------------------------------- progress ---

export function Progress({ value, className }: { value: number; className?: string }) {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div className={cn("h-1.5 w-full overflow-hidden rounded-full bg-ads-active", className)}>
      <div className="h-full rounded-full bg-ads-accent transition-[width]" style={{ width: `${pct}%` }} />
    </div>
  );
}

// ------------------------------------------------------------ separators ---

export function Separator({
  orientation = "horizontal",
  className,
}: {
  orientation?: "horizontal" | "vertical";
  className?: string;
}) {
  return (
    <div
      role="separator"
      className={cn(
        "bg-ads-border",
        orientation === "vertical" ? "h-full w-px" : "h-px w-full",
        className,
      )}
    />
  );
}

// ---------------------------------------------------------------- panels ---

/** A dockable side/bottom panel with a title bar, as in the mockups. */
export function Panel({
  title,
  actions,
  className,
  bodyClassName,
  children,
}: {
  title: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
  bodyClassName?: string;
  children: React.ReactNode;
}) {
  return (
    <section className={cn("flex min-h-0 flex-col bg-ads-surface", className)}>
      <header className="flex h-8 shrink-0 items-center justify-between border-b border-ads-border px-3">
        <span className="text-xs font-semibold text-ads-text">{title}</span>
        <div className="flex items-center gap-1">{actions}</div>
      </header>
      <div className={cn("min-h-0 flex-1 overflow-auto", bodyClassName)}>{children}</div>
    </section>
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-1 p-6 text-center">
      <p className="text-sm text-ads-muted">{title}</p>
      {hint ? <p className="text-xs text-ads-faint">{hint}</p> : null}
    </div>
  );
}
